import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
  ListSubheader,
  IconButton,
  Modal,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import jwt_decode from "jwt-decode";
import { ThemeProvider } from "@mui/material/styles";
import dashboardTheme from "../Dashboard/components/dashboardTheme";
import SearchIcon from "@mui/icons-material/Search";
import ListScans from "./Components/ListScans";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { formatUtcDateSimple } from "../../helpers/formatUtcDate";
import { getCspmChecks, clearAllCspmMessages, deleteCspmWorkload, initiateCspmScan, clearCspmCompare } from "../../store/cspm/cspmSlice";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import CreateWorkloadModal from './Components/CreateWorkloadModal';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditWorkloadModal from './Components/EditWorkloadModal';
import InitiateScanModal from './Components/InitiateScanModal';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CSPMGrid from './Components/CSPMGrid';
import { useFetchRemediableChecksOnce } from '../RemediationHub/components/common/hooks';
import ErrorUI from './Components/ErrorUI';
import CSPMHeader from './Components/CSPMHeader';
import ComparativeScan from './Components/ComparativeScan';
import { CloudProviderContext, getAccountCloudType } from "../../CloudProviderContext";
import CloudProviderMark from "../../components/Common/CloudProviderMark";
import { getListOfOrgsAndAccts } from "../../store/actions";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";

const CSPM_VIEW_TABS_SX = {
  minHeight: 40,
  width: "100%",
  "& .MuiTabs-flexContainer": {
    width: "100%",
    alignItems: "center",
    gap: 0.25,
  },
  "& .MuiTab-root": {
    textTransform: "none",
    minHeight: 40,
    minWidth: 0,
    flex: 1,
    maxWidth: "none",
    px: 2,
    fontSize: "0.8125rem",
    color: "#6b7280",
    fontWeight: 500,
    borderRadius: "8px",
    whiteSpace: "nowrap",
  },
  "& .MuiTab-root.Mui-selected": {
    color: "#111827 !important",
    fontWeight: 700,
    backgroundColor: "#ffffff",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#F98125",
    height: 3,
    borderRadius: "2px 2px 0 0",
  },
};

const scanHasChecks = (scan) => Boolean(scan?.auditChecks?.Checks?.length);

// Date loads merge into cspmResultOrderedMap. The dashboard renders selectedScan, so View must
// resolve the clicked row to the map entry that carries auditChecks.
const pickScanFromMap = (map, scan) => {
  if (!scan || !map) return null;
  const matches = [];
  const keyed = scan.scanDate ? map[scan.scanDate] : null;
  if (keyed) matches.push(keyed);
  Object.values(map).forEach((item) => {
    if (!item || matches.includes(item)) return;
    if (scan.runId && item.runId === scan.runId) matches.push(item);
    else if (scan.scanDate && item.scanDate === scan.scanDate) matches.push(item);
  });
  if (!matches.length) return null;
  return matches.find(scanHasChecks) || matches.find((item) => item.auditChecks) || matches[0];
};

const CSPM_VIEW_TAB_KEY = "cspmViewTab";

const getStoredCspmViewTab = () => {
  try {
    const stored = sessionStorage.getItem(CSPM_VIEW_TAB_KEY);
    if (stored === "single" || stored === "comparative") return stored;
  } catch (_) {
    // sessionStorage may be unavailable
  }
  return "single";
};

const CSPM_ACCENT = "#F98125";
const CSPM_FIELD_HEIGHT = 44;
const CSPM_TOOLBAR_CARD_SX = {
  backgroundColor: "#f3f4f6",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
};

const CSPMDashboard = () => {
  useFetchRemediableChecksOnce();
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const [isAccountConfigured, setIsAccountConfigured] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [scansModalOpen, setScansModalOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [currentCspmData, setCurrentCspmData] = useState([]);
  const [selectedWorkload, setSelectedWorkload] = useState("");
  const [initiateScanModalOpen, setInitiateScanModalOpen] = useState(false);
  const [createWorkloadModalOpen, setCreateWorkloadModalOpen] = useState(false);
  const [selectValue, setSelectValue] = useState(selectedWorkload || selectedAccount);
  const [availableDates, setAvailableDates] = useState([]);
  const [cspmResultOrderedMap, setCspmResultOrderedMap] = useState({});
  const [trendScoreByRunId, setTrendScoreByRunId] = useState({});
  const [selectedScanDate, setSelectedScanDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredAccount, setHoveredAccount] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [editWorkloadModalOpen, setEditWorkloadModalOpen] = useState(false);
  const [selectedWorkloadToEdit, setSelectedWorkloadToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorkloadToDelete, setSelectedWorkloadToDelete] = useState(null);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const apiCallInProgress = useRef(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [cspmViewTab, setCspmViewTab] = useState(getStoredCspmViewTab);
  const cspmViewTabRef = useRef(cspmViewTab);
  const previousProviderRef = useRef(selectedProvider);
  cspmViewTabRef.current = cspmViewTab;

  useEffect(() => {
    try {
      sessionStorage.setItem(CSPM_VIEW_TAB_KEY, cspmViewTab);
    } catch (_) {
      // ignore persistence failures
    }
  }, [cspmViewTab]);

  const selectedScanRef = useRef(null);
  selectedScanRef.current = selectedScan;

  // Selectors
  const { 
    isGettingCspmChecks, 
    getCspmChecksError, 
    getCspmChecksMessage,
    cspmChecks,
    isDeletingCspmWorkload,
    deleteCspmWorkloadMessage,
    deleteCspmWorkloadError,
    isInitiatingCspmScan,
    initiateCspmScanMessage,
    initiateCspmScanError
  } = useSelector((state) => state.cspm);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  function safeDispatchCspmChecks(payload) {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    dispatch(getCspmChecks(payload));
    setTimeout(() => { apiCallInProgress.current = false; }, 300);
  }

  useEffect(() => {
    document.title = 'CSPM'; // Make it uppercase here
  }, []);

  const providerAccounts = useMemo(() => {
    if (!accounts) return [];
    return accounts.filter(
      (acc) =>
        !acc.accountNumber.includes("INIT") &&
        getAccountCloudType(acc) === selectedProvider
    );
  }, [accounts, selectedProvider]);

  useEffect(() => {
    let userInfo;
    if (localStorage.getItem("authUser")) {
      const obj = JSON.parse(localStorage.getItem("authUser"));
      userInfo = jwt_decode(obj.idToken).email;
    }
    if (!accounts) return;

    const activeAccounts = providerAccounts.filter(
      (account) =>
        account.status === "ACTIVE" &&
        account.listusr?.some((user) => user.userName === userInfo)
    );

    if (activeAccounts.length > 0) {
      setIsAccountConfigured(true);
      setAccountStatus("ACTIVE");
    } else {
      setIsAccountConfigured(false);
      const inactiveAccounts = providerAccounts.filter(
        (account) =>
          account.status === "INACTIVE" && account.number !== "INIT00000001"
      );
      if (inactiveAccounts.length > 0) {
        setAccountStatus("INACTIVE");
      } else {
        setAccountStatus("NOACCOUNT");
      }
    }
  }, [accounts, providerAccounts, selectedProvider]);

  // Fetch CSPM data when account changes
  useEffect(() => {
    if (selectedAccount) {
      setAvailableDates([]);
      setCspmResultOrderedMap({});
      setTrendScoreByRunId({});
      setSelectedScan(null);
      setSelectedScanDate(null);
      setCurrentCspmData([]);
      safeDispatchCspmChecks({ accountNumber: selectedAccount });
    }
  }, [selectedAccount, dispatch]);

  // Handle API responses and errors
  useEffect(() => {
    // Only ignore when accounts have loaded and the selection is not in this provider list.
    // Do not treat "accounts still loading" (empty providerAccounts) as a mismatch — that
    // drops the checks response and leaves the UI empty until the user re-selects.
    if (
      selectedAccount &&
      providerAccounts.length > 0 &&
      !providerAccounts.some((acc) => acc.accountNumber === selectedAccount)
    ) {
      return;
    }
    if (getCspmChecksMessage && cspmChecks) {
      console.log("🚀 ~ useEffect ~ cspmChecks:", cspmChecks)
      const incomingMap = cspmChecks.cspmResultOrderedMap || {};
      setAvailableDates(cspmChecks.availableDates || []);
      // Merge so date-scoped loads (Comparative / ListScans) cannot wipe newer scans.
      // Account/workload changes clear the map before refetch.
      setCspmResultOrderedMap((prev) => ({ ...prev, ...incomingMap }));
      if (cspmChecks.trendScore) {
        const trendScore = cspmChecks.trendScore;
        const requested = cspmChecks.requestedScanDate
          ? String(cspmChecks.requestedScanDate)
          : "";
        const requestedDay = requested.replace("T", " ").slice(0, 10);
        const scans = Object.values(incomingMap).filter((scan) => scan?.runId);
        const exact = requested
          ? scans.filter((scan) => String(scan.scanDate || "") === requested)
          : [];
        const sameDay = requestedDay
          ? scans.filter((scan) => String(scan.scanDate || "").replace("T", " ").slice(0, 10) === requestedDay)
          : [];
        const targets = exact.length
          ? exact
          : sameDay.length
            ? sameDay
            : scans.filter((scan) => scan.auditChecks);
        if (targets.length) {
          setTrendScoreByRunId((prev) => {
            const next = { ...prev };
            targets.forEach((scan) => {
              next[scan.runId] = trendScore;
            });
            return next;
          });
        }
      }

      if (cspmViewTabRef.current === "comparative") {
        setDataLoaded(true);
      } else if (!selectedScanRef.current) {
        // Auto-pick latest only when nothing is selected (initial load / account change).
        let scans = Object.entries(incomingMap).map(([key, scan]) => ({
          ...scan,
          _key: key,
        }));

        if (!selectedWorkload) {
          scans = scans.filter(
            (scan) =>
              scan.runStatus === "Complete" &&
              scan.resourceScopeType === "Full Run"
          );
        } else {
          scans = scans.filter((scan) => scan.runStatus === "Complete");
        }

        scans.sort(
          (a, b) =>
            new Date(String(b.scanDate).replace(" ", "T")) -
            new Date(String(a.scanDate).replace(" ", "T"))
        );

        const latestFullRunScan = scans[0];
        if (latestFullRunScan) {
          console.log("🚀 ~ useEffect ~ latestFullRunScan:", latestFullRunScan);
          setSelectedScan(latestFullRunScan);
          setSelectedScanDate(latestFullRunScan.scanDate);
          setCurrentCspmData(latestFullRunScan.cspmOrderedList || []);
        } else {
          setSelectedScan(null);
          setSelectedScanDate(null);
          setCurrentCspmData([]);
        }
        setDataLoaded(true);
      } else {
        setDataLoaded(true);
      }
    }

    if (getCspmChecksError) {
      setSnackbar({
        open: true,
        message: getCspmChecksError,
        severity: "error",
        autoHideDuration: 6000,
      });
      setDataLoaded(true);
    }

    // Only clear success messages, not error messages
    if (getCspmChecksMessage) {
      dispatch(clearAllCspmMessages());
    }
  }, [
    getCspmChecksMessage,
    getCspmChecksError,
    cspmChecks,
    dispatch,
    selectedAccount,
    providerAccounts,
    selectedWorkload,
  ]);

  // Handle delete success/error
  useEffect(() => {
    if (deleteCspmWorkloadMessage) {
      setSnackbar({
        open: true,
        message: deleteCspmWorkloadMessage,
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      // Refresh CSPM list
      if (selectedAccount) {
        safeDispatchCspmChecks({ accountNumber: selectedAccount });
      }
    } else if (deleteCspmWorkloadError) {
      setSnackbar({
        open: true,
        message: deleteCspmWorkloadError,
        severity: 'error',
      });
    }
  }, [deleteCspmWorkloadMessage, deleteCspmWorkloadError, selectedAccount, dispatch]);

  // Debug logs before rendering the grid
  useEffect(() => {
    if (selectedScanDate && cspmResultOrderedMap) {
      // eslint-disable-next-line no-console
      console.log('selectedScanDate:', selectedScanDate);
      console.log('cspmResultOrderedMap keys:', Object.keys(cspmResultOrderedMap));
      console.log('scanObj:', cspmResultOrderedMap[selectedScanDate]);
    }
  }, [selectedScanDate, cspmResultOrderedMap]);

  // Handle scan selection. The row passed from the dialog can be a summary; the checks
  // payload for that run lives on the map entry filled by the date request.
  const handleScanSelect = (scan) => {
    const resolved = pickScanFromMap(cspmResultOrderedMap, scan) || scan;
    setSelectedScan(resolved);
    setCurrentCspmData(resolved?.cspmOrderedList || []);
    setSelectedScanDate(resolved?.scanDate || scan?.scanDate);
  };

  // Keep the rendered scan aligned with the map when a date request lands before or after View.
  useEffect(() => {
    if (!selectedScanDate || !cspmResultOrderedMap) return;
    const current = selectedScanRef.current;
    const resolved = pickScanFromMap(cspmResultOrderedMap, {
      scanDate: selectedScanDate,
      runId: current?.scanDate === selectedScanDate ? current.runId : undefined,
    });
    if (!resolved) return;

    setCurrentCspmData(resolved.cspmOrderedList || []);
    if (resolved === current) return;
    const sameScan =
      current &&
      ((resolved.runId && resolved.runId === current.runId) ||
        resolved.scanDate === current.scanDate);
    if (sameScan && scanHasChecks(current) && !scanHasChecks(resolved)) return;
    setSelectedScan(resolved);
  }, [selectedScanDate, cspmResultOrderedMap]);

  // On initial load, set selectedScanDate to latest available scan (use exact string from API)
  useEffect(() => {
    if (!selectedScanDate && Array.isArray(availableDates) && availableDates.length > 0) {
      // Use the exact string from availableDates (no conversion)
      const sortedDates = [...availableDates].sort((a, b) => new Date(b.replace(' ', 'T')) - new Date(a.replace(' ', 'T')));
      const latestValid = sortedDates.find(date => cspmResultOrderedMap && cspmResultOrderedMap[date]);
      if (latestValid) {
        setSelectedScanDate(latestValid);
      }
    }
  }, [availableDates, selectedScanDate, cspmResultOrderedMap]);

  // Filter out INIT accounts and sort by creation date (descending)
  const accountsWithWorkloads = useMemo(() => {
    let filtered = [...providerAccounts];
    if (filtered.length && filtered[0].creationDate) {
      filtered = filtered.sort(
        (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
      );
    }
    const accountWorkloadMap = cspmChecks?.accountWorkloadMap || {};
    return filtered.map((acc) => ({
      ...acc,
      schedules: accountWorkloadMap[acc.id] || [],
    }));
  }, [providerAccounts, cspmChecks]);

  useEffect(() => {
    dispatch(
      getListOfOrgsAndAccts({ params: { accountType: selectedProvider } })
    );
  
    setSelectedAccount("");
    setSelectedWorkload("");
    setSelectValue("");
    setSelectedScan(null);
    setCurrentCspmData([]);
    setAvailableDates([]);
    setCspmResultOrderedMap({});
    setTrendScoreByRunId({});
    setSelectedScanDate(null);
    setExpandedAccount(null);
    setDataLoaded(false);
    if (previousProviderRef.current !== selectedProvider) {
      setCspmViewTab("single");
      previousProviderRef.current = selectedProvider;
    }
    dispatch(clearCspmCompare());
  }, [selectedProvider, dispatch]);

  // Set default selected account on mount / when accounts become available
  useEffect(() => {
    if (!selectedAccount && accountsWithWorkloads.length > 0) {
      const accountNumber = accountsWithWorkloads[0].accountNumber;
      setSelectedAccount(accountNumber);
      setSelectedWorkload("");
      // Same explicit fetch as dropdown click so data loads on initial page entry
      safeDispatchCspmChecks({ accountNumber });
    }
  }, [accountsWithWorkloads, selectedAccount]);

  useEffect(() => {
    setSelectValue(selectedWorkload || selectedAccount);
  }, [selectedWorkload, selectedAccount]);

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setHoveredAccount(null);
  };

  const handleDropdownChange = (e) => {
    const value = e.target.value;
   
    // If value is an account, clear workload
    const isAccount = accountsWithWorkloads.some(acc => acc.accountNumber === value);
    if (isAccount) {
      setSelectedAccount(value);
      setSelectedWorkload("");
      setSelectValue(value);
    } else {
      // Find the parent account
      const parentAcc = accountsWithWorkloads.find(acc => 
        acc.schedules.some(schedule => schedule.scheduleId === value)
      );
      setSelectedAccount(parentAcc?.accountNumber || "");
      setSelectedWorkload(value);
      setSelectValue(value);
    }
  };

  // Restore renderAccountMessage
  const renderAccountMessage = () => {
    if (isAccountConfigured === null || isAccountConfigured) return null;
    const providerLabel = selectedProvider === "AZURE" ? "Azure" : "AWS";
    const messageContent =
      accountStatus === "INACTIVE" ? (
        <>
          All the {providerLabel} accounts in this organization are inactive. Please add a
          new {providerLabel} account or activate the inactive accounts through the{" "}
          <Typography
            component="span"
            onClick={() => (window.location.href = "/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      ) : (
        <>
          This organization does not have any {providerLabel} account on-boarded. Please add
          a {providerLabel} account through{" "}
          <Typography
            component="span"
            onClick={() => (window.location.href = "/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      );
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
          p: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: "600px",
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ color: "text.primary", mb: 2, fontWeight: 500 }}
          >
            Account Configuration Required
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            {messageContent}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Handle scan initiation success/error
  useEffect(() => {
    if (initiateCspmScanMessage) {
      setSnackbar({
        open: true,
        message: initiateCspmScanMessage,
        severity: 'success',
      });
      // Optionally refresh CSPM list
      if (selectedAccount) {
        safeDispatchCspmChecks({ accountNumber: selectedAccount });
      }
      dispatch(clearAllCspmMessages());
      setInitiateScanModalOpen(false);
    } else if (initiateCspmScanError) {
      setSnackbar({
        open: true,
        message: initiateCspmScanError,
        severity: 'error',
      });
      dispatch(clearAllCspmMessages());
    }
  }, [initiateCspmScanMessage, initiateCspmScanError, selectedAccount, dispatch]);

  // Expand submenu by default for the first account with workloads/schedules
  useEffect(() => {
    if (accountsWithWorkloads.length > 0) {
      const firstWithSubmenu = accountsWithWorkloads.find(acc => (acc.schedules && acc.schedules.length > 0));
      if (firstWithSubmenu) {
        setExpandedAccount(firstWithSubmenu.accountNumber);
      } else {
        setExpandedAccount(null);
      }
    }
  }, [accountsWithWorkloads]);

  // Early return if account is not configured
  if (!isAccountConfigured) {
    return renderAccountMessage();
  }

  console.log('Filtered CSPM data:', currentCspmData);

  // Add this helper to get display name for selected value
  const getSelectedDisplay = (val) => {
    if (!val) return '';
    let label = val;
    // If selectedWorkload is set, find its name
    if (selectedWorkload) {
      // Search workloads
      for (const acc of accountsWithWorkloads) {
        const wl = acc.schedules.find(s => s.scheduleId === selectedWorkload);
        if (wl) {
          label = wl.scheduleName;
          break;
        }
      }
      if (label === val) label = selectedWorkload;
    } else {
      // Otherwise, show account name/number
      const acc = accountsWithWorkloads.find(a => a.accountNumber === selectedAccount);
      if (acc) label = `${acc.accountName} (${acc.accountNumber})`;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <CloudProviderMark provider={selectedProvider} sx={{ mr: 0.75 }} />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      </Box>
    );
  };

  // Handle edit workload
  const handleEditWorkload = (workload) => {
    setSelectedWorkloadToEdit(workload);
    setEditWorkloadModalOpen(true);
    handleMenuClose();
  };

  // Handle delete workload
  const handleDeleteWorkload = (workload) => {
    setSelectedWorkloadToDelete(workload);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (selectedWorkloadToDelete) {
      dispatch(deleteCspmWorkload({ 
        workloadScheduleName: selectedWorkloadToDelete.scheduleName
      }));
    }
  };

  // Add this helper function before the return statement
  function getAccountNumberForWorkload(workload) {
    if (!workload) return '';
    for (const acc of accountsWithWorkloads) {
      if (acc.schedules.some(s => s.scheduleId === workload.scheduleId)) {
        return acc.accountNumber;
      }
    }
    return '';
  }

  const handleComparativeAccountSelect = (accountNumber) => {
    setSelectedAccount(accountNumber);
    setSelectedWorkload("");
    setSelectValue(accountNumber);
    safeDispatchCspmChecks({ accountNumber });
  };

  const handleComparativeWorkloadSelect = (accountNumber, scheduleId, scheduleName) => {
    setSelectedAccount(accountNumber);
    setSelectedWorkload(scheduleId);
    setSelectValue(scheduleId);
    setAvailableDates([]);
    setCspmResultOrderedMap({});
    safeDispatchCspmChecks({ accountNumber, workloadScheduleName: scheduleName });
  };

  return (
    <ThemeProvider theme={dashboardTheme}>
      <Box
        key={selectedProvider}
        sx={{
          padding: "12px",
          paddingBottom: 0,
          backgroundColor: "#F9F9F9",
          height: "calc(100vh - 116px)",
          maxHeight: "calc(100vh - 116px)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...ACCOUNT_DETAIL_FONT_SX,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: "100%",
            maxWidth: "100%",
            backgroundColor: "#f3f4f6",
            borderRadius: "10px",
            px: 0.5,
            py: 0.5,
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={cspmViewTab}
            onChange={(_, value) => setCspmViewTab(value)}
            variant="fullWidth"
            sx={CSPM_VIEW_TABS_SX}
          >
            <Tab value="single" label="CSPM Dashboard" />
            <Tab value="comparative" label="Compare CSPM Results" />
          </Tabs>
        </Box>

        <Box
          sx={{
            display: cspmViewTab === "single" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
        {/* Filters Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            flexWrap: "wrap",
            flexShrink: 0,
            ...CSPM_TOOLBAR_CARD_SX,
          }}
        >
            {accountsWithWorkloads.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220, maxWidth: 280, flex: "0 1 260px" }}>
                <Select
                  value={selectValue}
                  displayEmpty
                  aria-label="Account or workload"
                  onChange={handleDropdownChange}
                  renderValue={getSelectedDisplay}
                  onOpen={() => setSelectOpen(true)}
                  onClose={() => setSelectOpen(false)}
                  open={selectOpen}
                  sx={{
                    minWidth: 220,
                    height: CSPM_FIELD_HEIGHT,
                    fontSize: "0.875rem",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#111827",
                      borderWidth: "1px",
                    },
                    "& .MuiSelect-select": {
                      py: 0,
                      display: "flex",
                      alignItems: "center",
                      height: CSPM_FIELD_HEIGHT,
                      boxSizing: "border-box",
                      fontWeight: 500,
                      color: "#111827",
                    },
                    "& .MuiSelect-icon": {
                      color: "#94a3b8",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                        border: "1px solid #e2e8f0",
                        mt: 0.5,
                        background: "#fff",
                        minWidth: 320,
                        maxHeight: 360,
                      },
                    },
                  }}
                >
                  {accountsWithWorkloads.map(acc => (
                    <React.Fragment key={acc.accountNumber}>
                      <MenuItem
                        value={acc.accountNumber}
                        sx={{
                          pl: 2,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid #f0f4f8',
                          borderRadius: "6px",
                          mx: 0.5,
                          my: 0.25,
                          background: selectValue === acc.accountNumber ? 'rgba(249, 129, 37, 0.10)' : 'inherit',
                          "&:hover": { backgroundColor: "#fff4eb" },
                        }}
                        onClick={e => {
                          // Only select account if not clicking a workload
                          if (e.target.closest('.workload-submenu-item')) return;
                          setSelectedAccount(acc.accountNumber);
                          setSelectedWorkload("");
                          setSelectValue(acc.accountNumber);
                          setExpandedAccount(expandedAccount === acc.accountNumber ? null : acc.accountNumber);
                          safeDispatchCspmChecks({ accountNumber: acc.accountNumber });
                          setSelectOpen(false);
                        }}
                      >
                        <ListItemText primary={`${acc.accountName} (${acc.accountNumber})`} />
                        {(acc.schedules?.length > 0) && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              setExpandedAccount(expandedAccount === acc.accountNumber ? null : acc.accountNumber);
                            }}
                            sx={{ ml: 1 }}
                          >
                            {expandedAccount === acc.accountNumber ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </MenuItem>
                      {/* Inline expandable submenu for schedules */}
                      {expandedAccount === acc.accountNumber && acc.schedules.length > 0 && (
                        <Box sx={{
                          pl: 5,
                          py: 1,
                          background: '#f8fafc',
                          borderLeft: `3px solid ${CSPM_ACCENT}`,
                          borderRadius: 1,
                          mb: 1,
                        }}>
                          <Typography variant="caption" sx={{ color: CSPM_ACCENT, fontWeight: 700, mb: 0.5, pl: 0.5 }}>
                            Workloads
                          </Typography>
                          {acc.schedules.map((item) => (
                            <Box
                              key={item.scheduleId}
                              className="workload-submenu-item"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                background: selectValue === item.scheduleId ? 'rgba(249, 129, 37, 0.10)' : 'transparent',
                                mt: 0.5,
                                '&:hover': {
                                  background: '#fff4eb',
                                },
                                borderBottom: '1px solid #e2e8f0',
                                minHeight: 36,
                                cursor: 'pointer',
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedAccount(acc.accountNumber);
                                setSelectedWorkload(item.scheduleId);
                                setSelectValue(item.scheduleId);
                                safeDispatchCspmChecks({ accountNumber: acc.accountNumber, workloadScheduleName: item.scheduleName });
                                setSelectOpen(false);
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: '#111827',
                                  pl: 1,
                                  flex: 1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  pr: 2,
                                }}
                                title={item.scheduleName}
                              >
                                {item.scheduleName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, minWidth: 48, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleEditWorkload(item);
                                  }}
                                  sx={{ color: '#64748b', p: '4px', '&:hover': { bgcolor: 'rgba(249, 129, 37, 0.08)', color: CSPM_ACCENT } }}
                                >
                                  <EditIcon fontSize="small" style={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleDeleteWorkload(item);
                                  }}
                                  sx={{ color: '#d32f2f', p: '4px', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' } }}
                                >
                                  <DeleteIcon fontSize="small" style={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Scan Date Box */}
            <Box
              component="button"
              type="button"
              onClick={() => setScansModalOpen(true)}
              aria-label="Select scan date"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                flex: "0 0 auto",
                width: "auto",
                maxWidth: "100%",
                height: CSPM_FIELD_HEIGHT,
                px: 1.25,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: "#fff",
                cursor: "pointer",
                textAlign: "left",
                appearance: "none",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              <CalendarMonthIcon sx={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }} />
              <Box sx={{ flex: "0 0 auto", minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    lineHeight: 1.2,
                    mb: 0.15,
                  }}
                >
                  Scan
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: selectedScan?.scanDate ? "#111827" : "#94a3b8",
                    whiteSpace: "nowrap",
                    lineHeight: 1.3,
                  }}
                >
                  {selectedScan?.scanDate
                    ? formatUtcDateSimple(selectedScan.scanDate)
                    : "Select scan"}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={{ color: "#94a3b8", fontSize: 20, flexShrink: 0 }} />
            </Box>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, ml: { md: "auto" }, flexShrink: 0, flexWrap: "wrap" }}>
            {/* Create Workload Button — AWS only */}
            {selectedProvider !== "AZURE" && (
              <Tooltip title="Create Workload" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateWorkloadModalOpen(true)}
                  sx={{
                    height: CSPM_FIELD_HEIGHT,
                    borderRadius: "8px",
                    minWidth: 40,
                    px: 2,
                    py: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderColor: CSPM_ACCENT,
                    color: CSPM_ACCENT,
                    backgroundColor: "#fff",
                    "&:hover": {
                      borderColor: "#e8741c",
                      color: "#e8741c",
                      backgroundColor: "#fff7ed",
                    },
                  }}
                >
                  Workload
                </Button>
              </Tooltip>
            )}
            {/* Configure Scan Button */}
            <Tooltip title="Initiate Scan" arrow>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
                sx={{
                  height: CSPM_FIELD_HEIGHT,
                  borderRadius: "8px",
                  minWidth: 40,
                  px: 2.25,
                  py: 0,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  backgroundColor: CSPM_ACCENT,
                  boxShadow: "none",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#e8741c",
                    boxShadow: "none",
                  },
                }}
                onClick={() => setInitiateScanModalOpen(true)}
              >
                Initiate Scan
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/* CSPM Header */}
        <CSPMHeader
          scanObj={selectedScan}
          trendScore={
            (selectedScan?.runId && trendScoreByRunId[selectedScan.runId]) ||
            cspmChecks?.trendScore
          }
          isLoading={isGettingCspmChecks}
        />
        {/* CSPM Grid */}
        {isGettingCspmChecks ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="40vh"
          >
            <CircularProgress color="primary" />
          </Box>
        ) : getCspmChecksError ? (
          <ErrorUI
            error={getCspmChecksError}
            onRetry={() => safeDispatchCspmChecks({ accountNumber: selectedAccount })}
          />
        ) : (dataLoaded && !selectedScan) ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No checks found for this scan.
            </Typography>
          </Box>
        ) : (
          selectedScan && <CSPMGrid
            scanObj={selectedScan}
            selectedDate={selectedScanDate}
            trendScore={cspmChecks?.trendScore}
            accountNumber={selectedAccount}
          />
        )}
        </Box>
        </Box>

        <Box
          sx={{
            display: cspmViewTab === "comparative" ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <ComparativeScan
            accountsWithWorkloads={accountsWithWorkloads}
            selectValue={selectValue}
            selectedAccount={selectedAccount}
            selectedWorkload={selectedWorkload}
            selectedProvider={selectedProvider}
            onSelectAccount={handleComparativeAccountSelect}
            onSelectWorkload={handleComparativeWorkloadSelect}
            cspmResultOrderedMap={cspmResultOrderedMap}
            availableDates={availableDates}
            isGettingCspmChecks={isGettingCspmChecks}
            isActive={cspmViewTab === "comparative"}
          />
        </Box>

        {/* ListScans Modal */}
        <ListScans
          open={scansModalOpen}
          onClose={() => setScansModalOpen(false)}
          scans={Object.values(cspmResultOrderedMap || {}).filter(scan => scan && scan.scanDate)}
          selectedAccount={selectedAccount}
          onScanSelect={handleScanSelect}
          availableDates={availableDates}
          complianceResultOrderedMap={cspmResultOrderedMap}
          initialScanDate={selectedScan?.scanDate || selectedScanDate}
          nextRunDate={Object.values(cspmResultOrderedMap || {}).filter(scan => scan && scan.scanDate)[0]?.nextRunDate}
        />

        {/* Create Workload Modal */}
        <CreateWorkloadModal
          open={createWorkloadModalOpen}
          onClose={() => setCreateWorkloadModalOpen(false)}
          accounts={accountsWithWorkloads}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            // Refresh CSPM list data
            if (selectedAccount) {
              safeDispatchCspmChecks({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Initiate Scan Modal */}
        <InitiateScanModal
          open={initiateScanModalOpen}
          onClose={() => setInitiateScanModalOpen(false)}
          accountsWithWorkloads={accountsWithWorkloads}
          selectedAccount={selectedAccount}
          selectedWorkload={selectedWorkload}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            if (selectedAccount) {
              safeDispatchCspmChecks({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Edit Workload Modal */}
        <EditWorkloadModal
          open={editWorkloadModalOpen}
          onClose={() => setEditWorkloadModalOpen(false)}
          workload={selectedWorkloadToEdit}
          accountNumber={getAccountNumberForWorkload(selectedWorkloadToEdit)}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            if (selectedAccount) {
              safeDispatchCspmChecks({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Delete Dialog */}
        <Modal
          open={deleteDialogOpen}
          onClose={() => {
            if (!isDeletingCspmWorkload) {
              setDeleteDialogOpen(false);
            }
          }}
          aria-labelledby="delete-workload-dialog"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: '10px',
              boxShadow: 24,
              p: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#F5F5F5',
                padding: '16px 24px',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                borderBottom: '1px solid #E0E0E0',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Delete Workload
              </Typography>
              <IconButton 
                onClick={() => {
                  if (!isDeletingCspmWorkload) {
                    setDeleteDialogOpen(false);
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Are you sure you want to delete the workload<br />
                <b>{selectedWorkloadToDelete?.scheduleName}</b>?
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    if (!isDeletingCspmWorkload) {
                      setDeleteDialogOpen(false);
                    }
                  }}
                  disabled={isDeletingCspmWorkload}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleConfirmDelete}
                  disabled={isDeletingCspmWorkload}
                  startIcon={isDeletingCspmWorkload ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isDeletingCspmWorkload ? 'Deleting...' : 'Delete'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default CSPMDashboard; 